/**
 * Notification Sound Utility
 * Plays audio notifications for incoming messages
 */

class NotificationSound {
  private audioContext: AudioContext | null = null;
  private isMuted: boolean = false;

  constructor() {
    // Initialize AudioContext on user interaction to comply with browser autoplay policies
    if (typeof window !== 'undefined') {
      this.initAudioContext();
    }
  }

  private initAudioContext() {
    try {
      const AudioContextClass =
        window.AudioContext || (window as any).webkitAudioContext;
      this.audioContext = new AudioContextClass();
    } catch (error) {
      console.error('Web Audio API not supported:', error);
    }
  }

  /**
   * Play a notification sound using Web Audio API
   * Creates a short ding sound
   */
  public async play() {
    if (this.isMuted) return;

    // Lazy init audio context if it wasn't created at module load
    if (!this.audioContext) {
      this.initAudioContext();
    }

    if (!this.audioContext) return;

    try {
      // Resume audio context if suspended (browser autoplay policy)
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }
      const now = this.audioContext.currentTime;

      // Debug: surface audioContext state in dev builds
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.debug(
          '[notificationSound] play() audioContext.state=',
          this.audioContext?.state
        );
      }

      // Create a short, pleasant ding sound
      this.createBeep(now, 800, 0.08, 0.5);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error('[notificationSound] play() failed', error);
      }
      // Silent fail in production
    }
  }

  /**
   * Helper method to create a single beep
   */
  private createBeep(
    startTime: number,
    frequency: number,
    duration: number,
    volume: number
  ) {
    if (!this.audioContext) return;

    try {
      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();

      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(frequency, startTime);

      // Sharp attack and release for more intrusive sound
      gainNode.gain.setValueAtTime(0, startTime);
      gainNode.gain.linearRampToValueAtTime(volume, startTime + 0.01);
      gainNode.gain.exponentialRampToValueAtTime(0.001, startTime + duration);

      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext.destination);

      oscillator.start(startTime);
      oscillator.stop(startTime + duration);
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * Play a notification sound for mentions
   * Creates a double ding sound
   */
  public async playMention() {
    if (this.isMuted) return;

    // Lazy init audio context if it wasn't created at module load
    if (!this.audioContext) {
      this.initAudioContext();
    }

    if (!this.audioContext) return;

    try {
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }
      const now = this.audioContext.currentTime;

      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.debug(
          '[notificationSound] playMention() audioContext.state=',
          this.audioContext?.state
        );
      }

      // Create double ding pattern for mentions
      // First ding
      this.createBeep(now, 800, 0.08, 0.5);

      // Second ding
      this.createBeep(now + 0.15, 800, 0.08, 0.5);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error('[notificationSound] playMention() failed', error);
      }
    }
  }

  /**
   * Mute notification sounds
   */
  public mute() {
    this.isMuted = true;
    localStorage.setItem('chatSoundsMuted', 'true');
  }

  /**
   * Unmute notification sounds
   */
  public unmute() {
    this.isMuted = false;
    localStorage.setItem('chatSoundsMuted', 'false');
  }

  /**
   * Toggle mute state
   */
  public toggleMute() {
    if (this.isMuted) {
      this.unmute();
    } else {
      this.mute();
    }
    return this.isMuted;
  }

  /**
   * Check if sounds are muted
   */
  public getMuteState(): boolean {
    return this.isMuted;
  }

  /**
   * Load mute state from localStorage
   */
  public loadMuteState() {
    const savedState = localStorage.getItem('chatSoundsMuted');
    this.isMuted = savedState === 'true';
  }

  /**
   * Enable audio context on user interaction (for browsers with autoplay restrictions)
   */
  public async enableAudio(): Promise<void> {
    try {
      if (!this.audioContext) {
        this.initAudioContext();
      }

      if (this.audioContext && this.audioContext.state === 'suspended') {
        // Resume may return a promise in some browsers
        const res = this.audioContext.resume();
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.debug('[notificationSound] enableAudio() resume called', res);
        }
        try {
          await res;
          if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.debug('[notificationSound] enableAudio() resume resolved');
          }
        } catch (e) {
          if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.error(
              '[notificationSound] enableAudio() resume rejected',
              e
            );
          }
        }
      }
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error('[notificationSound] enableAudio() failed', e);
      }
    }
  }

  /**
   * Development helper: expose internal debug info (audioContext state, mute)
   */
  public debugInfo() {
    return {
      audioContextState: this.audioContext ? this.audioContext.state : null,
      isMuted: this.isMuted,
    };
  }

  /**
   * Test notification sound - useful for debugging
   * Can be called from console: window.testNotificationSound()
   */
  public test() {
    this.play();
  }

  /**
   * Test mention sound - useful for debugging
   * Can be called from console: window.testMentionSound()
   */
  public testMention() {
    this.playMention();
  }
}

// Export singleton instance
export const notificationSound = new NotificationSound();

// Load saved mute state
notificationSound.loadMuteState();

// Enable audio on first user interaction
if (typeof document !== 'undefined') {
  const enableAudio = () => {
    notificationSound.enableAudio();
    document.removeEventListener('click', enableAudio);
    document.removeEventListener('keydown', enableAudio);
  };

  document.addEventListener('click', enableAudio, { once: true });
  document.addEventListener('keydown', enableAudio, { once: true });
}

// Development-only: expose helpers for quick testing in the browser console
if (process.env.NODE_ENV !== 'production' && typeof window !== 'undefined') {
  try {
    // attach under window for quick manual testing from console
    (window as any).notificationSound = notificationSound;
    (window as any).testNotificationSound = () => notificationSound.test();
    (window as any).testMentionSound = () => notificationSound.testMention();
    (window as any).notificationSoundDebug = () =>
      notificationSound.debugInfo();
    // eslint-disable-next-line no-console
  } catch (e) {
    // ignore in environments where window cannot be written to
  }
}
